import React, { useCallback, useEffect, useRef, useState } from "react";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";
import { getComponentWithLoading } from "@commons/Utils";
import useMessage from "@commons/Hooks/useMessage";
import { ChatComposer } from "./ChatComposer";
import { ChatMensaje } from "./ChatMensaje";
import { ChatRecientes } from "./ChatRecientes";
import {
    abrirChat,
    buscarConversaciones,
    iniciarChat,
    nuevaConversacion,
    preguntar,
    preguntarNative,
    recuperarNative,
    historialNative,
    guardarObservacionNative
} from "../../Services/asistenteService";
import "./AsistenteChat.css";

const AsistenteSL = ({ showLoading, hideLoading }) => {
    const { Warning, Error: MostrarError } = useMessage();

    const [configurado, setConfigurado] = useState(true);
    const [puedeRevisar, setPuedeRevisar] = useState(false);
    const [conversacion, setConversacion] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [recientes, setRecientes] = useState([]);
    const [pagina, setPagina] = useState(0);
    const [hayMas, setHayMas] = useState(false);
    const [cargandoMas, setCargandoMas] = useState(false);
    const [bienvenida, setBienvenida] = useState("");
    const [pregunta, setPregunta] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [bloqueo, setBloqueo] = useState(null);
    const [pilotoNative, setPilotoNative] = useState(false);
    const [coordinacionOpenAi, setCoordinacionOpenAi] = useState(false);
    const [contabilidadNative, setContabilidadNative] = useState(false);
    const [dgiiPracticoNative, setDgiiPracticoNative] = useState(false);
    const [dgiiPracticoOperativoNative, setDgiiPracticoOperativoNative] = useState(false);
    const [dgiiSuspendidoNative, setDgiiSuspendidoNative] = useState(false);
    const [pendienteNative, setPendienteNative] = useState(null);
    const [lecturaAutomaticaDetenida, setLecturaAutomaticaDetenida] = useState(false);
    const puedeCambiarChat = !pendienteNative || pendienteNative.puedeContinuarEnOtroChat === true;

    const hiloRef = useRef(null);
    const enviandoRef = useRef(false);
    const recuperacionEnCursoRef = useRef(false);
    const pendienteRef = useRef(null);
    const ambitoSesionRef = useRef(null);
    const recuperarActualRef = useRef(null);
    const recuperacionAutomaticaRef = useRef({ clave: null, lecturas: 0, inicio: 0, detenido: false });
    const componenteVigenteRef = useRef(true);
    const contextoRecuperacionRef = useRef(null);
    enviandoRef.current = enviando;
    contextoRecuperacionRef.current = `${conversacion?.Id}:${pendienteNative?.requestId || ""}`;
    useEffect(() => () => { componenteVigenteRef.current = false; }, []);

    // El hilo se mantiene abajo: lo último respondido es lo que interesa leer.
    useEffect(() => {
        const hilo = hiloRef.current;
        if (hilo) hilo.scrollTop = hilo.scrollHeight;
    }, [mensajes, enviando, pendienteNative?.requestId]);

    const aplicar = useCallback((datos) => {
        if (!datos) return;
        if (/^[a-f0-9]{64}$/.test(datos.AmbitoSesion || "")) ambitoSesionRef.current = datos.AmbitoSesion;

        if (datos.Conversacion !== undefined) setConversacion(datos.Conversacion);
        if (datos.Mensajes !== undefined) setMensajes(datos.Mensajes || []);
        if (datos.Bienvenida !== undefined) setBienvenida(datos.Bienvenida || "");
        if (datos.PuedeRevisar !== undefined) setPuedeRevisar(Boolean(datos.PuedeRevisar));
        if (datos.Configurado !== undefined) setConfigurado(Boolean(datos.Configurado));
        if (datos.PilotoNative !== undefined) setPilotoNative(Boolean(datos.PilotoNative));
        if (datos.CoordinacionOpenAi !== undefined) setCoordinacionOpenAi(datos.CoordinacionOpenAi === true);
        if (datos.ContabilidadNative !== undefined) setContabilidadNative(Boolean(datos.ContabilidadNative));
        if (datos.DgiiPracticoNative !== undefined) setDgiiPracticoNative(Boolean(datos.DgiiPracticoNative));
        if (datos.DgiiPracticoOperativoNative !== undefined) setDgiiPracticoOperativoNative(Boolean(datos.DgiiPracticoOperativoNative));
        if (datos.DgiiSuspendidoNative !== undefined) setDgiiSuspendidoNative(Boolean(datos.DgiiSuspendidoNative));
    }, []);

    // Only opaque request/conversation identifiers are stored in the browser; no question or answer.
    const pendienteKey = () => ambitoSesionRef.current ? `asistente-citrus-pending:${ambitoSesionRef.current}` : null;
    const guardarPendiente = (value) => {
        const actual = pendienteRef.current;
        if (actual === value || (actual && value && Object.keys({ ...actual, ...value })
            .every(key => actual[key] === value[key]))) return;
        pendienteRef.current = value;
        setPendienteNative(value);
        try {
            const key = pendienteKey();
            if (key && value) sessionStorage.setItem(key, JSON.stringify({ conversacionId: value.conversacionId, requestId: value.requestId }));
            else if (key) sessionStorage.removeItem(key);
        } catch (_) { /* The server remains the durable source even if browser storage is unavailable. */ }
    };
    const cargarNative = async (id, base = [], vigente = () => true) => {
        const datos = await historialNative(id);
        if (!vigente()) return;
        const registrados = datos?.MensajesNative || [];
        const ids = new Set(registrados.map(m => String(m.Id)));
        const locales = base.filter(m => String(m.Id).startsWith("native:") && !ids.has(String(m.Id)));
        const todos = [...base.filter(m => !String(m.Id).startsWith("native:")), ...locales, ...registrados];
        todos.sort((a, b) => new Date(a.FechaCreacion) - new Date(b.FechaCreacion));
        setMensajes(todos);
        const pendiente = registrados.filter(m => m.Native?.PuedeRecuperar).slice(-1)[0];
        if (pendiente) guardarPendiente({ conversacionId: id, requestId: pendiente.Native.RequestId,
            estado: pendiente.Native.Estado,
            actualizacionAutomatica: pendiente.Native.ActualizacionAutomatica === true,
            esperaActualizacionMs: pendiente.Native.EsperaActualizacionMs,
            puedeContinuarEnOtroChat: pendiente.Native.PuedeContinuarEnOtroChat === true });
        else if (pendienteRef.current?.conversacionId === id && registrados.some(m =>
            m.Native?.RequestId === pendienteRef.current.requestId && m.Native.PuedeRecuperar === false)) guardarPendiente(null);
    };
    const mostrarResultadoNative = (datos, texto, base) => {
        const clave = `native:${datos.RequestId}:`;
        const anterior = base.find(m => String(m.Id) === clave + "assistant");
        // Reading a result must never remove and then restore its user message.
        const nuevos = base.filter(m => String(m.Id) !== clave + "assistant");
        const fecha = new Date().toISOString();
        if (texto && !nuevos.some(m => String(m.Id) === clave + "user")) nuevos.push({ Id: clave + "user", EsAsistente: false, Remitente: "Usuario", TextoNative: texto, FechaCreacion: fecha });
        nuevos.push({ Id: clave + "assistant", EsAsistente: true, Remitente: "Asistente Citrus", ContenidoHtml: datos.ContenidoHtml, Native: datos, FechaCreacion: anterior?.FechaCreacion || fecha });
        setMensajes(nuevos);
        return nuevos;
    };

    // Un fallo de permiso o de compañía no se reintenta: deja la pantalla explicada y quieta.
    const manejarFallo = useCallback((error, { bloquea = false } = {}) => {
        const mensaje = error?.Mensaje
            || "Ocurrió un error al comunicarse con el asistente. Intente de nuevo más tarde.";

        if (bloquea) {
            setBloqueo(mensaje);
            return;
        }
        // El servidor clasifica: Tipo "Warning" es algo que el usuario puede corregir,
        // "Error" es un fallo de negocio, e Interno es una excepcion no prevista.
        if (error?.Interno || error?.Tipo === "Error") {
            MostrarError({ message: mensaje });
            return;
        }
        Warning({ message: mensaje });
    }, [MostrarError, Warning]);

    /**
     * Trae una pagina del historial. Con acumular se agrega al final (boton Ver mas);
     * sin el, reemplaza el listado (primera carga o refresco tras una accion).
     */
    const cargarHistorial = useCallback(async (paginaPedida, acumular) => {
        const datos = await buscarConversaciones(paginaPedida);
        const lista = datos?.Conversaciones || [];

        setRecientes((actual) => (acumular ? [...actual, ...lista] : lista));
        setPagina(datos?.Pagina ?? paginaPedida);
        setHayMas(Boolean(datos?.HayMas));
    }, []);

    const verMas = async () => {
        if (cargandoMas || !hayMas) return;

        setCargandoMas(true);
        try {
            await cargarHistorial(pagina + 1, true);
        } catch (error) {
            manejarFallo(error);
        } finally {
            setCargandoMas(false);
        }
    };

    // Tras enviar, abrir o crear se refresca la primera pagina: la conversacion pudo
    // estrenar titulo o subir al tope del listado.
    const refrescarHistorial = useCallback(() => {
        cargarHistorial(0, false).catch(() => {});
    }, [cargarHistorial]);

    useEffect(() => {
        let vigente = true;

        showLoading();
        Promise.all([iniciarChat(), cargarHistorial(0, false)])
            .then(async ([datos]) => {
                if (!vigente) return;
                aplicar(datos);
                if (datos?.PilotoNative && datos?.Conversacion?.Id) {
                    try {
                        const saved = JSON.parse((pendienteKey() && sessionStorage.getItem(pendienteKey())) || "null");
                        if (saved && saved.conversacionId === datos.Conversacion.Id) guardarPendiente({ ...saved, actualizacionAutomatica: true });
                    } catch (_) { /* No automatic resend or recovery on malformed local state. */ }
                    await cargarNative(datos.Conversacion.Id, datos.Mensajes || []);
                }
            })
            .catch((error) => { if (vigente) manejarFallo(error, { bloquea: true }); })
            .finally(() => { if (vigente) hideLoading(); });

        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enviar = async () => {
        const texto = pregunta.trim();
        if (!texto || enviando || !conversacion?.Id || pendienteNative) return;

        setEnviando(true);
        try {
            let datos;
            if (pilotoNative) {
                // Secure browser randomness, with no weak/time based GUID fallback.
                const bytes = new Uint8Array(16);
                window.crypto.getRandomValues(bytes);
                const requestId = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
                guardarPendiente({ conversacionId: conversacion.Id, requestId, actualizacionAutomatica: true });
                datos = await preguntarNative(conversacion.Id, texto, requestId);
                const visibles = mostrarResultadoNative(datos, texto, mensajes);
                // A history reload failure must not erase the response already received.
                await cargarNative(conversacion.Id, visibles).catch(manejarFallo);
                if (!datos?.PuedeRecuperar) guardarPendiente(null);
                else guardarPendiente({ conversacionId: conversacion.Id, requestId, estado: datos?.Estado,
                    actualizacionAutomatica: datos?.ActualizacionAutomatica === true, esperaActualizacionMs: datos?.EsperaActualizacionMs,
                    puedeContinuarEnOtroChat: datos?.PuedeContinuarEnOtroChat === true });
            } else {
                datos = await preguntar(conversacion.Id, texto);
                aplicar(datos);
            }
            setPregunta("");
            refrescarHistorial();

            if (datos?.Advertencia) {
                Warning({ message: datos.Advertencia });
            }
        } catch (error) {
            // La pregunta no se borra: el usuario puede corregirla y reintentar sin reescribirla.
            if (pilotoNative && error?.SolicitudNoEnviada === true) guardarPendiente(null);
            manejarFallo(error);
        } finally {
            setEnviando(false);
        }
    };

    const recuperarPendiente = async ({ vigente = () => true } = {}) => {
        if (!pendienteNative || enviandoRef.current || recuperacionEnCursoRef.current) return;
        const pendiente = pendienteNative;
        recuperacionEnCursoRef.current = true;
        enviandoRef.current = true;
        try {
            const datos = await recuperarNative(pendiente.conversacionId, pendiente.requestId);
            if (!vigente()) return;
            recuperacionAutomaticaRef.current.fallos = 0;
            const espera = Math.max(3000, Math.min(600000, datos?.EsperaActualizacionMs || 3000));
            // Anchor the normal cadence to dispatch, not receipt; otherwise a
            // three-second interval skips its next tick and becomes six seconds.
            recuperacionAutomaticaRef.current.proxima = espera > 3000 ? Date.now() + espera
                : (recuperacionAutomaticaRef.current.ultimaLectura || Date.now()) + espera;
            // A progress snapshot is not a new message: keep the thread and its
            // scroll position intact, and avoid reloading all history on each GET.
            if (datos?.ActualizacionAutomatica === true) {
                guardarPendiente({ ...pendiente, estado: datos.Estado, actualizacionAutomatica: true,
                    esperaActualizacionMs: datos.EsperaActualizacionMs,
                    puedeContinuarEnOtroChat: datos.PuedeContinuarEnOtroChat === true });
                return;
            }
            // Publish the received result immediately; history synchronization
            // must not extend the loading or temporarily erase the question.
            mostrarResultadoNative(datos, "", mensajes);
            if (!datos?.PuedeRecuperar && datos?.Estado !== "no_encontrada") guardarPendiente(null);
            else {
                guardarPendiente({ ...pendiente, estado: datos?.Estado,
                    actualizacionAutomatica: datos?.ActualizacionAutomatica === true,
                    esperaActualizacionMs: datos?.EsperaActualizacionMs,
                    puedeContinuarEnOtroChat: datos?.PuedeContinuarEnOtroChat === true });
            }
        } catch (error) {
            if (vigente()) {
                const control = recuperacionAutomaticaRef.current;
                control.fallos = (control.fallos || 0) + 1;
                control.proxima = Date.now() + Math.min(30000, 3000 * Math.pow(2, control.fallos));
                if (control.fallos >= 6 || error?.SolicitudNoEnviada === true) {
                    control.detenido = true;
                    setLecturaAutomaticaDetenida(true);
                }
            }
        } finally {
            recuperacionEnCursoRef.current = false;
            enviandoRef.current = false;
        }
    };
    recuperarActualRef.current = recuperarPendiente;

    const ultimaRespuestaPendiente = [...mensajes].reverse().find(m => m.Native?.RequestId === pendienteNative?.requestId)?.Native;
    useEffect(() => {
        if (!coordinacionOpenAi || !pendienteNative || !pendienteNative.actualizacionAutomatica
            || pendienteNative.conversacionId !== conversacion?.Id) return;
        const clave = `${pendienteNative.conversacionId}:${pendienteNative.requestId}`;
        if (recuperacionAutomaticaRef.current.clave !== clave) {
            recuperacionAutomaticaRef.current = { clave, lecturas: 0, inicio: Date.now(), detenido: false, fallos: 0, proxima: 0 };
            setLecturaAutomaticaDetenida(false);
        }
        let vigente = true;
        const timer = setInterval(() => {
            const control = recuperacionAutomaticaRef.current;
            if (!vigente || control.clave !== clave || control.detenido) { clearInterval(timer); return; }
            if (control.lecturas >= 200 || Date.now() - control.inicio >= 600000) {
                control.detenido = true;
                setLecturaAutomaticaDetenida(true);
                clearInterval(timer);
                return;
            }
            if (enviandoRef.current || recuperacionEnCursoRef.current || Date.now() < control.proxima) return;
            control.lecturas++;
            control.ultimaLectura = Date.now();
            // This is only the existing recovery endpoint, never another PreguntarNative.
            recuperarActualRef.current({
                vigente: () => componenteVigenteRef.current && contextoRecuperacionRef.current === clave });
        }, 3000);
        return () => { vigente = false; clearInterval(timer); };
        // Current callback/data live in refs; a request's own busy state must not cancel its read.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coordinacionOpenAi, pendienteNative?.requestId, pendienteNative?.conversacionId, pendienteNative?.actualizacionAutomatica, conversacion?.Id]);

    const registrarObservacion = async (requestId, texto, feedbackId) => {
        const datos = await guardarObservacionNative(conversacion.Id, requestId, texto, feedbackId);
        if (datos?.ObservacionesUsuario) {
            setMensajes(actual => actual.map(m => m.Native?.RequestId === requestId
                ? { ...m, Native: { ...m.Native, ObservacionesUsuario: datos.ObservacionesUsuario } } : m));
        }
        return datos;
    };

    const abrir = async (id) => {
        if (enviando || !puedeCambiarChat || id === conversacion?.Id) return;

        showLoading();
        try {
            const datos = await abrirChat(id);
            aplicar(datos);
            guardarPendiente(null);
            if (pilotoNative) await cargarNative(id, datos.Mensajes || []);
            setPregunta("");
            refrescarHistorial();
        } catch (error) {
            manejarFallo(error);
        } finally {
            hideLoading();
        }
    };

    const comenzarNueva = async () => {
        if (enviando || !puedeCambiarChat) return;

        // Sin preguntas el chat actual ya esta vacio: abrir otro no aporta nada y
        // avisarlo seria reganar al usuario por un clic inofensivo. El boton ademas
        // se muestra deshabilitado, asi que esto es solo la red de seguridad.
        if (!mensajes.length) return;

        showLoading();
        try {
            aplicar(await nuevaConversacion());
            guardarPendiente(null);
            setPregunta("");
            refrescarHistorial();
        } catch (error) {
            manejarFallo(error);
        } finally {
            hideLoading();
        }
    };

    const soloLectura = !configurado || Boolean(bloqueo) || !conversacion?.EsActiva || Boolean(pendienteNative);
    const preparandoOpenAi = coordinacionOpenAi && Boolean(pendienteNative?.actualizacionAutomatica) && !lecturaAutomaticaDetenida;
    // El motivo del bloqueo cambia el texto del campo: no es lo mismo que el asistente
    // este apagado a que el chat que abrio el usuario ya este cerrado.
    const motivoSoloLectura = !configurado || bloqueo
        ? "El asistente no está disponible."
        : pendienteNative
            ? preparandoOpenAi
                ? "Estoy preparando tu respuesta; aparecerá aquí automáticamente."
                : "No pude confirmar la respuesta. Se conservó la consulta para revisión; no la vuelvas a enviar."
            : "Este chat está cerrado. Abra uno nuevo para seguir preguntando.";

    return (
        <div className="bootstrapiso">
            <div className="citrus-gpt">
                <div className="citrus-gpt-title">
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span className="citrus-gpt-icon">
                            <SvgComponent iconName="chat" width="22px" height="22px" fill="currentColor" />
                        </span>
                        <div>
                            <h1>{pilotoNative ? "Asistente Citrus" : "Asistente de Documentación"}</h1>
                            {pilotoNative && <span className="citrus-gpt-tag citrus-gpt-tag--neutra">Documentación de Citrus, DGII y Contabilidad · Sin navegación web</span>}
                            <p>{pilotoNative ? (dgiiSuspendidoNative ? "Consulte sobre el funcionamiento documentado de Citrus y sobre contabilidad y finanzas. Recibirá una explicación con fuentes y las aclaraciones necesarias para su caso." : `Consulte sobre Citrus y documentación DGII${contabilidadNative ? ", además de contabilidad y finanzas" : ""}. Recibirá una explicación con las fuentes y las aclaraciones necesarias para su caso.`) : "Consulte la documentación del ERP y los casos ya validados por el equipo."}</p>
                        </div>
                    </div>

                    <div className="citrus-gpt-title__actions">
                        {puedeRevisar && !pilotoNative && (
                            <a
                                className="citrus-gpt-btn citrus-gpt-btn--link"
                                href="/Seguridad/conocimiento_ia_list.aspx"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <SvgComponent iconName="notes" width="15px" height="15px" fill="currentColor" />
                                Revisar respuestas
                            </a>
                        )}
                        <button
                            type="button"
                            className="citrus-gpt-btn"
                            onClick={comenzarNueva}
                            disabled={!configurado || Boolean(bloqueo) || !puedeCambiarChat || enviando || mensajes.length === 0}
                            title="Cierra la conversación actual y abre una nueva"
                        >
                            <SvgComponent iconName="plus" width="14px" height="14px" fill="currentColor" />
                            Nuevo chat
                        </button>
                    </div>
                </div>

                {bloqueo && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--error" role="alert">
                        <SvgComponent iconName="alert" width="18px" height="18px" fill="currentColor" />
                        <span>{bloqueo}</span>
                    </div>
                )}

                {!bloqueo && !configurado && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--aviso" role="alert">
                        <SvgComponent iconName="alert" width="18px" height="18px" fill="currentColor" />
                        <span>El asistente no está configurado. Comuníquese con el administrador.</span>
                    </div>
                )}

                {!bloqueo && configurado && conversacion && !conversacion.EsActiva && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--aviso" role="alert">
                        <SvgComponent iconName="info" width="18px" height="18px" fill="currentColor" />
                        <span>
                            Está viendo un chat cerrado. Abra uno nuevo para seguir preguntando.
                        </span>
                    </div>
                )}

                {!bloqueo && (
                    <div className="citrus-gpt-layout">
                        <ChatRecientes
                            conversaciones={recientes}
                            actualId={conversacion?.Id}
                            onAbrir={abrir}
                            ocupado={enviando}
                            hayMas={hayMas}
                            onVerMas={verMas}
                            cargando={cargandoMas}
                        />

                        <div className="citrus-gpt-panel">
                            <div className="citrus-gpt-hilo" ref={hiloRef}>
                                {mensajes.length === 0 && (
                                    <div
                                        className="citrus-gpt-vacio"
                                        dangerouslySetInnerHTML={{ __html: bienvenida }}
                                    />
                                )}

                                {mensajes.filter(mensaje => !(preparandoOpenAi && mensaje.EsAsistente
                                    && mensaje.Native?.RequestId === pendienteNative.requestId)).map((mensaje) => (
                                    <ChatMensaje
                                        key={mensaje.Id}
                                        mensaje={pilotoNative && pendienteNative && mensaje.EsAsistente
                                            && mensaje.Native?.RequestId === pendienteNative.requestId && !mensaje.Native.Publicable
                                            ? { ...mensaje, Native: { ...mensaje.Native, ContenidoHtml: preparandoOpenAi
                                                ? "<p>Estoy preparando tu respuesta; aparecerá aquí automáticamente.</p>"
                                                : "<p>No pude confirmar la respuesta. La consulta y su información quedaron conservadas para revisión técnica. No vuelvas a enviar esta misma pregunta.</p>" } }
                                            : mensaje}
                                        puedeRevisar={puedeRevisar}
                                        onGuardarObservacion={pilotoNative ? registrarObservacion : undefined}
                                    />
                                ))}

                                {pilotoNative && pendienteNative && !enviando && !preparandoOpenAi && (!ultimaRespuestaPendiente || ultimaRespuestaPendiente.Publicable) && (
                                    <div className="citrus-gpt-msg citrus-gpt-msg--asistente" role="status" aria-live="polite">
                                        <span className="citrus-gpt-msg__remitente">Asistente Citrus</span>
                                        <div className="citrus-gpt-msg__burbuja">{preparandoOpenAi
                                            ? "Estoy preparando tu respuesta; aparecerá aquí automáticamente."
                                            : "No pude confirmar la respuesta. La consulta y su información quedaron conservadas para revisión técnica. No vuelvas a enviar esta misma pregunta."}</div>
                                    </div>
                                )}

                                {(enviando || preparandoOpenAi) && (
                                    <div className="citrus-gpt-msg citrus-gpt-msg--asistente">
                                        <span className="citrus-gpt-msg__remitente">{pilotoNative ? "Asistente Citrus" : "Asistente Citrus"}</span>
                                        <div className="citrus-gpt-msg__burbuja citrus-gpt-pensando" role="status" aria-label="Preparando respuesta" aria-live="polite">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <ChatComposer
                                valor={pregunta}
                                onChange={setPregunta}
                                onEnviar={enviar}
                                ocupado={enviando || preparandoOpenAi}
                                deshabilitado={soloLectura}
                                motivo={motivoSoloLectura}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const Asistente = getComponentWithLoading(AsistenteSL);
